import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconBellFill18 } from "nucleo-ui-fill-18"

import type {
	NotificationCategory,
	NotificationType,
} from "@productlane/lib/src/browser"
import type { NotificationPreferencePatch } from "@productlane/zero/mutators/notifications"
import {
	desktopNotificationsEnabled,
	NOTIFICATION_CATEGORIES,
	NOTIFICATION_CATEGORY_COPY,
	NOTIFICATION_TYPE_COPY,
	NOTIFICATION_TYPES,
	notificationTypeKeys,
	parseNotificationSettings,
	resolveTypePreference,
} from "@productlane/lib/src/browser"
import { NucleoFillIcon, Select, Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ScopeOptionValue } from "./notification-scope-options"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { useBrowserNotifications } from "@/lib/hooks/useBrowserNotifications"
import { generateId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"
import { NOTIFICATION_SETTINGS_COPY as copy } from "@/screens/app/notifications/copy"
import { SettingsHeader } from "../settings-header"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import {
	isOnOffScope,
	patchForScopeOption,
	scopeOptionsFor,
	scopeOptionValue,
} from "./notification-scope-options"

export function NotificationInboxSettingsPage() {
	const zero = useZero()
	const [preference] = useQuery(queries.notificationPreference())
	const [views] = useQuery(queries.customViews())
	const settings = useMemo(
		() => parseNotificationSettings(preference?.settings),
		[preference?.settings],
	)

	const byCategory = useMemo(() => {
		const map = new Map<NotificationCategory, NotificationType[]>()
		for (const type of notificationTypeKeys()) {
			const { category } = NOTIFICATION_TYPES[type]
			map.set(category, [...(map.get(category) ?? []), type])
		}
		return map
	}, [])

	const { isSupported, permission, requestPermission } =
		useBrowserNotifications()
	const desktopEnabled = desktopNotificationsEnabled(settings)

	const setDesktop = async (enabled: boolean) => {
		if (enabled) {
			if (!isSupported) {
				toast.info({ title: copy.desktopUnsupported, icon: IconBellFill18 })
				return
			}
			const result =
				permission === "granted" ? permission : await requestPermission()
			if (result !== "granted") {
				toast.info({ title: copy.desktopBlocked, icon: IconBellFill18 })
				return
			}
		}
		try {
			await mutateOk(
				zero.mutate(
					mutators.notificationPreferences.setDesktop({
						id: preference?.id ?? generateId(),
						enabled,
					}),
				),
			)
		} catch {
			toast.error({ title: copy.saveFailed })
		}
	}

	const patch = async (
		type: NotificationType,
		value: NotificationPreferencePatch,
	) => {
		try {
			await mutateOk(
				zero.mutate(
					mutators.notificationPreferences.patch({
						id: preference?.id ?? generateId(),
						type,
						patch: value,
					}),
				),
			)
		} catch {
			toast.error({ title: copy.saveFailed })
		}
	}

	return (
		<>
			<MetaInfo title={copy.pageTitle} />
			<SettingsHeader title={copy.pageTitle} subtitle={copy.pageSubtitle} />

			<SettingsContainer>
				<SettingItem
					title={copy.desktopTitle}
					description={copy.desktopDescription}
					action={
						<Switch
							name="notification-desktop"
							aria-label={copy.desktopTitle}
							checked={desktopEnabled}
							onCheckedChange={(checked) => void setDesktop(checked)}
						/>
					}
				/>
			</SettingsContainer>

			{NOTIFICATION_CATEGORIES.map((category) => {
				const types = byCategory.get(category) ?? []
				if (types.length === 0) return null
				return (
					<div key={category}>
						<div className="pb-3 pt-8 text-sm font-medium">
							{NOTIFICATION_CATEGORY_COPY[category].label}
						</div>
						<SettingsContainer>
							{types.map((type, index) => {
								const def = NOTIFICATION_TYPES[type]
								const pref = resolveTypePreference(type, settings)
								const options = scopeOptionsFor(def, views)
								return (
									<SettingItem
										key={type}
										className={index > 0 ? "pt-4" : undefined}
										title={NOTIFICATION_TYPE_COPY[type]?.label ?? type}
										description={NOTIFICATION_TYPE_COPY[type]?.description}
										action={
											isOnOffScope(options) ? (
												<Switch
													name={`notification-${type}`}
													aria-label={
														NOTIFICATION_TYPE_COPY[type]?.label ?? type
													}
													checked={pref.inbox !== "off"}
													onCheckedChange={(checked) =>
														void patch(type, {
															inbox: checked ? "involved" : "off",
														})
													}
												/>
											) : (
												<Select
													name={`notification-${type}`}
													className="w-40 flex-none"
													popoverAlign="end"
													value={scopeOptionValue(pref)}
													options={options.map((option) => ({
														value: option.value,
														groupLabel: option.groupLabel,
														label: option.icon ? (
															<span className="flex min-w-0 items-center gap-2">
																<NucleoFillIcon
																	icon={option.icon}
																	className="h-3.5 w-3.5 flex-none"
																/>
																<span className="truncate">{option.label}</span>
															</span>
														) : (
															option.label
														),
													}))}
													onChange={(value) =>
														void patch(
															type,
															patchForScopeOption(value as ScopeOptionValue),
														)
													}
												/>
											)
										}
									/>
								)
							})}
						</SettingsContainer>
					</div>
				)
			})}
		</>
	)
}
